import { useCallback, useEffect, useRef, useState } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'

import { useAuth } from 'src/auth'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { FIND_ACTIVITY_LOG_COMPANY_FOR_SWITCH } from 'src/graphql/activityLogs.graphql'
import { UPDATE_CURRENT_EMPLOYEE_ID } from 'src/graphql/users.graphql'

export interface CallCompanySwitchTarget {
  activityLogId: string
  applicantName?: string | null
  companyId: string
  companyName: string
  employeeId: string
}

/**
 * Lets an internal admin who opens a link to another company's call switch
 * to that company instead of being bounced. Everyone else (or a call the
 * user has no employee record for) gets `onNotFound`.
 */
export const useCallCompanySwitch = (
  activityLogId: string | null,
  opened: boolean,
  onNotFound: () => void
) => {
  const { currentUser } = useAuth()
  const [switchTarget, setSwitchTarget] =
    useState<CallCompanySwitchTarget | null>(null)
  const resolvedActivityLogIdRef = useRef<string | null>(null)
  // Bumped whenever the drawer closes or shows another call, so a lookup
  // still in flight from an earlier opening can't prompt or redirect.
  const sessionRef = useRef(0)

  useEffect(() => {
    sessionRef.current += 1
    resolvedActivityLogIdRef.current = null
    setSwitchTarget(null)
  }, [activityLogId, opened])

  const [findActivityLogCompany, { loading: isResolving }] = useLazyQuery(
    FIND_ACTIVITY_LOG_COMPANY_FOR_SWITCH,
    { fetchPolicy: 'network-only' }
  )

  const [updateCurrentEmployeeId, { loading: isSwitching }] = useMutation(
    UPDATE_CURRENT_EMPLOYEE_ID,
    {
      onCompleted: (data) => {
        if (!data?.updateCurrentEmployeeId) {
          toast('Unable to switch companies. Please try again.', 'error')
          return
        }
        window.location.reload()
      },
      onError: () => {
        toast('Unable to switch companies. Please try again.', 'error')
      },
    }
  )

  const handleCallNotFound = useCallback(() => {
    if (!activityLogId || !currentUser?.isInternalAdmin) {
      onNotFound()
      return
    }
    if (resolvedActivityLogIdRef.current === activityLogId) {
      return
    }
    resolvedActivityLogIdRef.current = activityLogId
    const session = sessionRef.current
    findActivityLogCompany({ variables: { activityLogId } })
      .then(({ data }) => data?.findActivityLogCompanyForSwitch ?? null)
      .catch(() => null)
      .then((target) => {
        if (session !== sessionRef.current) return
        if (target) {
          setSwitchTarget(target)
        } else {
          onNotFound()
        }
      })
  }, [currentUser, activityLogId, findActivityLogCompany, onNotFound])

  const confirmSwitch = useCallback(() => {
    if (!switchTarget) return
    updateCurrentEmployeeId({
      variables: { input: { currentEmployeeId: switchTarget.employeeId } },
    })
  }, [switchTarget, updateCurrentEmployeeId])

  const cancelSwitch = useCallback(() => {
    setSwitchTarget(null)
    onNotFound()
  }, [onNotFound])

  return {
    handleCallNotFound,
    switchTarget,
    isResolving,
    isSwitching,
    confirmSwitch,
    cancelSwitch,
  }
}

export default useCallCompanySwitch
